//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

// === STYLES ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === HOOKS ===
import { useLogout } from 'components/useLogout';
import { useInactiveTimeout } from 'components/hooks/useInactiveTimeout';

// === API ===
import { prokeyApi } from 'api/prokey';

// === COMPONENTS ===
import { Menu             } from 'pages/Menu';
import { TaskVerification } from 'pages/TaskVerification';
import { Admin            } from 'pages/Admin/Admin';
import { Stats            } from 'pages/Stats/Stats';
import { OcrOnDemand      } from 'pages/OcrOnDemand/OcrOnDemand';
import { WorkflowMonitor  } from 'pages/WorkflowMonitor';
import { DocAnalytics     } from 'pages/DocAnalytics/DocAnalytics';
import { TaskVerifyBar    } from 'pages/Menu/TaskVerifyBar/TaskVerifyBar';
import { TaskVerifyHeader } from 'pages/Menu/components/DocAnalytics/TaskVerifyHeader';
import { AuditQC          } from 'pages/AuditQC';

import { FilesHeader      } from 'pages/Menu/components/DocAnalytics/FilesHeader';
import { RulesHeader      } from 'pages/Menu/components/DocAnalytics/RulesHeader';

// === API ===
import { onInactive, onReconnectFail, removeListener } from 'api/prokey/prokey.websocket';

var pjson = require('../../../package.json');

//#endregion

//-----------------------------------------------------------------
export function PageManager() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const logout   = useLogout();
  const theme    = useTheme();
  
  useInactiveTimeout();

  const PAGE_OCROD      = 'onDemand'         ;
  const PAGE_VERIFY     = 'verify'           ;
  const PAGE_WFMON      = 'workflow-monitor' ;
  const PAGE_STATS      = 'stats'            ;
  const PAGE_ADMIN      = 'admin'            ;
  const PAGE_DA         = 'analytics'        ;
  const PAGE_DA_FILES   = 'da-files'         ;
  const PAGE_DA_RULES   = 'da-rules'         ;
  const PAGE_DA_VERIFY  = 'da-verify'        ;
  const PAGE_DA_VERIFY2 = 'da-verify2'       ;
  const PAGE_AUDIT_QC   = 'audit-qc'         ;


  const _pages = {
    [ PAGE_OCROD    ]: <OcrOnDemand      /> ,
    [ PAGE_VERIFY   ]: <TaskVerification /> ,
    [ PAGE_WFMON    ]: <WorkflowMonitor  /> ,
    [ PAGE_STATS    ]: <Stats            /> ,
    [ PAGE_ADMIN    ]: <Admin            /> ,
    [ PAGE_DA       ]: <DocAnalytics     /> ,
    [ PAGE_AUDIT_QC ]: <AuditQC          /> ,

  }

  const _paths = {
    [ PAGE_OCROD      ]: 'on-demand'   ,
    [ PAGE_VERIFY     ]: 'verify'      ,
    [ PAGE_WFMON      ]: 'workflow'    ,
    [ PAGE_STATS      ]: 'statistics'  ,
    [ PAGE_ADMIN      ]: 'admin'       ,
    [ PAGE_DA         ]: 'analytics/*' ,
    [ PAGE_DA_FILES   ]: 'analytics/files' ,
    [ PAGE_DA_RULES   ]: 'analytics/rules' ,
    [ PAGE_DA_VERIFY  ]: 'analytics/files/:file_id/exceptions/:exception_id' ,
    [ PAGE_DA_VERIFY2 ]: 'analytics/files/:file_id/task' ,
    [ PAGE_AUDIT_QC   ]: 'audit-qc',

  }

  //#endregion

  //-----------------------------------------------------------------
  async function logVersionInfo() {
    //-----------------------------------------------------------------
    const response = await prokeyApi.getVersion();
    console.log('PdWebServer version: '  + response.data);
    console.log('ProkeyClient version: ' + pjson.version);
  }


  useEffect( () => {
    
    //-----------------------------------------------------------------
    function handleReconnectFail() { logout();   }
      //-----------------------------------------------------------------

    //-----------------------------------------------------------------
    function handleInactive     () { logout(); }
      //-----------------------------------------------------------------

    onReconnectFail( handleReconnectFail );
    onInactive     ( handleInactive      );
    
    logVersionInfo();

    return () => {
      removeListener( 'reconnect-fail' , handleReconnectFail );
      removeListener( 'inactive'       , handleInactive      );
    }
  }, [] );

  //#region [DISPLAY]

  function display() {
    return (
      <Box sx={ {width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: theme.palette.panel.main } }>
        <Menu>
          <Routes>
            <Route path={ _paths[ PAGE_VERIFY    ] } element={ <TaskVerifyBar /> } />
            <Route path={ _paths[ PAGE_DA_VERIFY ] } element={ <TaskVerifyHeader /> } />
            <Route path={ _paths[ PAGE_DA_VERIFY2] } element={ <TaskVerifyHeader /> } />
            <Route path={ _paths[ PAGE_DA_FILES  ] } element={ <FilesHeader      /> } />
            <Route path={ _paths[ PAGE_DA_RULES  ] } element={ <RulesHeader      /> } />
            <Route path={ _paths[ PAGE_OCROD     ] } element={ null } />
            <Route path={ _paths[ PAGE_WFMON     ] } element={ null } />
            <Route path={ _paths[ PAGE_STATS     ] } element={ null } />
            <Route path={ _paths[ PAGE_ADMIN     ] } element={ null } />
            <Route path={ _paths[ PAGE_AUDIT_QC  ] } element={ null } />
          </Routes>
        </Menu>
        <Routes>
          <Route path={ _paths[ PAGE_VERIFY   ] } element={ <TaskVerification />} />
          <Route path={ _paths[ PAGE_DA       ] } element={ <DocAnalytics     />} />
          <Route path={ _paths[ PAGE_OCROD    ] } element={ <OcrOnDemand      />} />
          <Route path={ _paths[ PAGE_WFMON    ] } element={ <WorkflowMonitor  />} />
          <Route path={ _paths[ PAGE_STATS    ] } element={ <Stats            />} />
          <Route path={ _paths[ PAGE_ADMIN    ] } element={ <Admin            />} />
          <Route path={ _paths[ PAGE_AUDIT_QC ] } element={ <AuditQC          />} />
        </Routes>
      </Box>
    );
  }

  //#endregion

  return display();
}
